import {
  COGNITO_CALLBACK_URL,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
} from '@config/config';

/**
 * Static OIDC metadata for the Cognito Hosted UI domain.
 * Cognito's .well-known/openid-configuration does not send CORS headers, so
 * the browser cannot fetch it. Providing metadata here avoids that request.
 * Endpoints follow Cognito's Hosted UI pattern: {domain}/oauth2/{path}.
 * jwks_uri is omitted because Cognito serves it at cognito-idp.{region}.amazonaws.com/{poolId},
 * not at the Hosted UI domain; we only need authorization_endpoint for signinRedirect().
 */
function getCognitoMetadata() {
  if (!COGNITO_DOMAIN) return undefined;
  const base = COGNITO_DOMAIN.replace(/\/$/, '');
  return {
    issuer: base,
    authorization_endpoint: `${base}/oauth2/authorize`,
    token_endpoint: `${base}/oauth2/token`,
    userinfo_endpoint: `${base}/oauth2/userInfo`,
    end_session_endpoint: `${base}/logout`,
  };
}

/**
 * Cognito OIDC configuration for react-oidc-context.
 *
 * The redirect_uri points to the backend callback endpoint; the backend
 * exchanges the code for tokens, sets auth cookies, and redirects to the app.
 * So the OIDC context is used only to initiate sign-in (signinRedirect), not
 * to handle the callback on the frontend.
 *
 * We pass static metadata to avoid fetching .well-known/openid-configuration
 * from the browser, which Cognito blocks with CORS.
 */
export const cognitoOidcConfig = {
  authority: COGNITO_DOMAIN,
  client_id: COGNITO_CLIENT_ID,
  redirect_uri: COGNITO_CALLBACK_URL,
  response_type: 'code' as const,
  scope: 'email openid profile aws.cognito.signin.user.admin',
  metadata: getCognitoMetadata(),
};
